/**
 * The store's orders as staff see them (store checkout, owner 2026-10-18; the server is apps/api/src/store-orders.js).
 *
 * Members buy in the app; Vipps only RESERVES the money. Staff TAKE it by marking the order handed over («Utlevert»,
 * picked up at the mosque) or sent («Sendt», shipped); they can release a reservation («Kanseller og frigi») and give
 * money back («Refunder»). An approved order not handed over is released by the server after 6 days; staff are told at 4.
 *
 * Pure: words, amounts and the request id that makes a retried click the same operation. No React, no fetch.
 */
import type { TagTone } from '../components/ui';
import { Shipping, Store, type Carrier, type ShippingRate } from '../core';

/* ------------------------------------------------------------------ money */

const nok = new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK' });
/** øre → "kr 149,50" (the panel's money format). */
export const money = (ore: number) => nok.format(ore / 100);
/** øre → what staff type back in: "149" or "149,50". */
export const krText = (ore: number) => (ore % 100 ? (ore / 100).toFixed(2).replace('.', ',') : String(ore / 100));

/** A sum typed in kroner ("149", "149,50") → øre, within [min, max]. The words name the field. */
export function parseKr(text: string, { min = 0, max, what = 'Beløpet' }: { min?: number; max: number; what?: string }):
  { ok: true; ore: number } | { ok: false; message: string } {
  const p = Store.parseKroner(text);
  if (!p.ok) return { ok: false, message: `${what} skrives i kroner, for eksempel 149 eller 149,50.` };
  if (p.ore < min || p.ore > max) {
    return { ok: false, message: min > 0 ? `${what} må være fra ${krText(min)} til ${krText(max)} kr.` : `${what} kan være høyst ${krText(max)} kr.` };
  }
  return { ok: true, ore: p.ore };
}

/** The «Lager» field: empty = not counted (null), else a whole number 0–100 000 (core store.product). */
export const MAX_STOCK = 100_000;
export function parseStock(text: string): { ok: true; stock: number | null } | { ok: false; message: string } {
  const s = String(text ?? '').trim().replace(/[\s ]/g, '');
  if (!s) return { ok: true, stock: null };
  if (!/^\d{1,6}$/.test(s) || Number(s) > MAX_STOCK) return { ok: false, message: 'Lager er et helt tall fra 0 til 100 000, eller tomt (telles ikke).' };
  return { ok: true, stock: Number(s) };
}
/** What the product list says about stock: nothing when not counted. */
export function stockTag(stock: number | null | undefined): [string, TagTone] | null {
  if (stock === null || stock === undefined) return null;
  return stock === 0 ? ['Utsolgt', 'red'] : [`${new Intl.NumberFormat('nb-NO').format(stock)} igjen`, stock <= 3 ? 'gold' : 'grey'];
}

/* ------------------------------------------------------------------ words */

export type Delivery = 'pickup' | 'ship' | null | undefined;
export const DELIVERY_NO: Record<string, string> = { pickup: 'Henting', ship: 'Sending', ticket: 'Billett' };

/** An order's status as one word and a colour. `fulfilled` says how it went: handed over or sent. */
export function statusWord(status: string, delivery?: Delivery): [string, TagTone] {
  switch (status) {
    case 'draft': return ['Utkast', 'grey'];
    case 'awaiting_payment': return ['Venter på betaling', 'grey'];
    case 'paid': return ['Betalt – skal utleveres', 'gold'];
    case 'fulfilled': return [delivery === 'ship' ? 'Sendt' : 'Utlevert', 'green'];
    case 'cancelled': return ['Kansellert', 'grey'];
    case 'expired': return ['Utløpt', 'grey'];
    case 'refunded': return ['Refundert', 'grey'];
    case 'partly_refunded': return ['Delvis refundert', 'gold'];
    default: return [status, 'grey'];
  }
}

/** The list's filter chips, each a set of statuses. Drafts (staff-made, never paid) appear under «Alle» only. */
export const ORDER_FILTERS: { id: string; label: string; statuses: string[] | null }[] = [
  { id: 'all', label: 'Alle', statuses: null },
  { id: 'awaiting', label: 'Venter på betaling', statuses: ['awaiting_payment'] },
  { id: 'paid', label: 'Betalt – skal utleveres', statuses: ['paid'] },
  { id: 'fulfilled', label: 'Utlevert/sendt', statuses: ['fulfilled'] },
  { id: 'closed', label: 'Kansellert/utløpt', statuses: ['cancelled', 'expired'] },
  { id: 'refunded', label: 'Refundert', statuses: ['refunded', 'partly_refunded'] },
];
export function inFilter(status: string, filterId: string): boolean {
  const f = ORDER_FILTERS.find((x) => x.id === filterId);
  return !f || !f.statuses || f.statuses.includes(status);
}

/** The payment's status at Vipps, in staff words (core store-checkout PAY_STATUSES). */
export const PAY_NO: Record<string, string> = {
  create_pending: 'opprettes', create_unknown: 'uavklart – sjekkes', not_created: 'ikke opprettet', awaiting_user: 'venter på kunden',
  aborted: 'avbrutt av kunden', expired: 'utløpt', terminated: 'avsluttet', authorized: 'reservert', cancelled: 'frigitt',
  captured: 'trukket', partly_refunded: 'delvis refundert', refunded: 'refundert',
};

/** One operation in the order's history. */
export function operationWord(op: { kind: string; amountOre: number; state: string }, delivery?: Delivery): string {
  const what = op.kind === 'capture' ? `${delivery === 'ship' ? 'Sendt' : 'Utlevert'} – ${money(op.amountOre)} trukket`
    : op.kind === 'cancel' ? (op.amountOre > 0 ? `Frigitt – ${money(op.amountOre)}` : 'Kansellert før betaling')
    : op.kind === 'refund' ? `Refundert – ${money(op.amountOre)}` : op.kind;
  const state = op.state === 'pending' ? 'venter på svar fra Vipps' : op.state === 'failed' ? 'avvist av Vipps' : 'fullført';
  return `${what} (${state})`;
}

/* ------------------------------------------------------------------ handover deadline */

const DAY = 86_400_000;
/** Staff are told at 4 days; at 6 days the server releases what is reserved (core store-checkout). */
export const HANDOVER_ALERT_DAYS = 4;
export const AUTO_RELEASE_DAYS = 6;

/** A paid order old enough to be urgent: how old, and when it is released. null when not (yet) urgent. */
export function handoverDue(order: { status: string; createdAt: string }, now: Date): { days: number; releaseAt: Date } | null {
  if (order.status !== 'paid') return null;
  const made = new Date(order.createdAt).getTime();
  if (!Number.isFinite(made)) return null;
  const age = now.getTime() - made;
  if (age < HANDOVER_ALERT_DAYS * DAY) return null;
  return { days: Math.floor(age / DAY), releaseAt: new Date(made + AUTO_RELEASE_DAYS * DAY) };
}

/* ------------------------------------------------------------------ one operation, one request id */

export type OpKind = 'handover' | 'cancel' | 'refund';

/** A request id the server accepts: 8–64 of [A-Za-z0-9-]. */
export const REQUEST_ID = /^[A-Za-z0-9-]{8,64}$/;
export function newRequestId(rand: () => string = defaultRandom): string {
  const id = rand();
  return REQUEST_ID.test(id) ? id : defaultRandom();
}
function defaultRandom(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < b.length; i++) b[i] = Math.floor(Math.random() * 256);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}

/**
 * The request id of each attempt: one per order, kind and (for a refund) amount. A double click, or «Prøv igjen»
 * after no answer, sends the SAME id, so the server answers with the same operation instead of making a second one.
 * When the server has given a final answer (done or refused), the attempt is over and the next press gets a new id.
 */
export class Attempts {
  private ids = new Map<string, string>();
  constructor(private rand: () => string = defaultRandom) {}
  static key(orderId: string, kind: OpKind, amountOre?: number | null) { return `${orderId}:${kind}:${kind === 'refund' ? amountOre ?? '' : ''}`; }
  idFor(key: string): string {
    let id = this.ids.get(key);
    if (!id) { id = newRequestId(this.rand); this.ids.set(key, id); }
    return id;
  }
  /** After an answer: keep the id while the outcome is unknown (no answer, 5xx, 202), drop it when it is final. */
  settle(key: string, status: number) { if (!keepAttempt(status)) this.ids.delete(key); }
  has(key: string) { return this.ids.has(key); }
}
/** Whether an answer leaves the attempt open (the same request is safe and right to send again). */
export const keepAttempt = (status: number) => status === 0 || status === 202 || status >= 500;

/** What staff are told after an operation. */
export function outcomeText(kind: OpKind, status: number, body: Record<string, any>, delivery?: Delivery): { text: string; tone: 'ok' | 'warn' | 'err' } {
  const amount = body && body.operation && typeof body.operation.amountOre === 'number' ? body.operation.amountOre : null;
  if (status === 200) {
    if (kind === 'handover') return { tone: 'ok', text: `${delivery === 'ship' ? 'Sendt' : 'Utlevert'}. ${amount !== null ? money(amount) + ' er trukket.' : 'Pengene er trukket.'}` };
    if (kind === 'cancel') return { tone: 'ok', text: 'Ordren er kansellert og reservasjonen frigitt. Kunden belastes ikke.' };
    return { tone: 'ok', text: `${amount !== null ? money(amount) : 'Beløpet'} er refundert til kunden.` };
  }
  if (status === 202) return { tone: 'warn', text: 'Venter på svar fra Vipps – prøves igjen automatisk.' };
  const said = (body && (body.message || body.message_no)) || '';
  if (status === 0 || status >= 500) return { tone: 'err', text: `${said || 'Fikk ikke svar.'} Trykk «Prøv igjen» – det sendes som samme forespørsel.` };
  return { tone: 'err', text: said || 'Det gikk ikke.' };
}

/* ---------------------------------------------------------------- audit fixes 2026-10-19 */

/** The seller fields in words, in the order they are asked; core store-checkout SELLER_REQUIRED are the starred ones. */
export const SELLER_NO: Record<string, string> = { name: 'Juridisk navn', orgNo: 'Organisasjonsnummer', address: 'Postadresse', email: 'E-post', phone: 'Telefon' };

/** The seller form's fields from a stored seller: every field a string, a missing one empty. */
export type SellerFields = { name: string; orgNo: string; address: string; email: string; phone: string };
export function sellerFields(seller: Partial<Record<keyof SellerFields, string | null>> | null | undefined): SellerFields {
  const v = (x: string | null | undefined) => x || '';
  return { name: v(seller?.name), orgNo: v(seller?.orgNo), address: v(seller?.address), email: v(seller?.email), phone: v(seller?.phone) };
}
/** Whether the form differs from what is stored (spaces at the ends do not count). */
export function sellerDirty(form: SellerFields, stored: SellerFields): boolean {
  return (Object.keys(stored) as (keyof SellerFields)[]).some((k) => form[k].trim() !== stored[k].trim());
}

/** «Butikken er stengt» while a required seller detail is missing — said where staff look first, naming what is missing. */
export function sellerWarning(s: { sellerMissing?: string[] } | null | undefined): string | null {
  const missing = (s && s.sellerMissing) || [];
  if (!missing.length) return null;
  return `Medlemmene kan ikke bestille før selgeropplysningene er fylt inn. Mangler: ${missing.map((k) => SELLER_NO[k] || k).join(', ')}. `
    + 'Kjøperen skal se hvem som selger og hvordan selgeren kan nås, før de betaler (angrerettloven). Fyll dem inn under «Levering».';
}

/**
 * Why an order cannot be handled here, or was closed by itself (audit F-12; server store-orders closeStranded): its
 * payment belongs to a Vipps MSN the mosque no longer uses, or the mosque's Vipps keys are gone.
 */
export const NOTICE_NO: Record<string, string> = {
  msn_mismatch: 'Betalingen hører til et Vipps-brukersted moskeen ikke lenger bruker, og kan ikke behandles her. Ordren lukkes av seg selv '
    + '(ubetalt etter 2 timer, betalt etter 6 dager) og varene legges tilbake på lager. Sjekk ordren i Vipps-portalen.',
  closed_msn_changed: 'Lukket automatisk: betalingen hører til et Vipps-brukersted moskeen ikke lenger bruker. Ingenting er trukket herfra, '
    + 'og varene er lagt tilbake på lager. En reservasjon som ikke trekkes, går ut av seg selv; sjekk i Vipps-portalen.',
  closed_no_vipps: 'Lukket automatisk: moskeen har ikke lenger Vipps satt opp, så betalingen kunne ikke leses. Ingenting er trukket, '
    + 'og varene er lagt tilbake på lager. En reservasjon som ikke trekkes, går ut av seg selv; sjekk i Vipps-portalen.',
};

/* ---------------------------------------------------------------- shop shipping (2026-10-31) */

/** The carriers in words (core shipping LABELS, Norwegian). */
export const CARRIER_NO: Record<string, string> = Object.fromEntries(Shipping.CARRIERS.map((c) => [c, Shipping.label(c, 'no') || c]));
export const SHIPPING_MODE_NO: Record<string, string> = { flat: 'Fast frakt (én pris)', carrier: 'Frakt med transportør (pris per transportør og vekt)' };

/** One row of the rate table as staff type it. */
export interface RateRow { carrier: Carrier; maxKg: string; price: string }
export const rateRow = (r: ShippingRate): RateRow => ({ carrier: r.carrier, maxKg: gramsText(r.maxWeightG), price: krText(r.priceOre) });
/** 2000 → "2", 1500 → "1,5" (kilograms, as staff read «inntil 2 kg»). */
export const gramsText = (g: number) => (g % 1000 ? (g / 1000).toFixed(3).replace(/0+$/, '').replace('.', ',') : String(g / 1000));
/** «2», «1,5», «0.25» kg → grams, 1 … 100 000; else null. */
export function parseKg(text: string): number | null {
  const s = String(text ?? '').trim().replace(/[\s\u00a0]/g, '').replace(',', '.');
  if (!/^\d{1,3}(?:\.\d{1,3})?$/.test(s)) return null;
  const g = Math.round(Number(s) * 1000);
  return g >= 1 && g <= Shipping.MAX_BAND_WEIGHT_G ? g : null;
}
/** The typed rows → what PUT /store/shipping/rates takes, checked by core; the first problem named with its row. */
export function parseRates(rows: RateRow[]): { ok: true; items: ShippingRate[] } | { ok: false; message: string } {
  const items: ShippingRate[] = [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]!;
    const g = parseKg(r.maxKg);
    if (g === null) return { ok: false, message: `Rad ${i + 1}: skriv vekten «inntil» i kilo, for eksempel 2 eller 0,5 (høyst 100 kg).` };
    const p = parseKr(r.price, { max: Shipping.MAX_RATE_ORE, what: `Rad ${i + 1}: prisen` });
    if (!p.ok) return { ok: false, message: p.message };
    items.push({ carrier: r.carrier, maxWeightG: g, priceOre: p.ore });
  }
  const v = Shipping.rates(items);
  return v.ok ? { ok: true, items: v.value } : { ok: false, message: v.message };
}
/** Whether the typed table differs from the stored one (compared as what would be saved). */
export function ratesDirty(rows: RateRow[], stored: ShippingRate[]): boolean {
  const typed = parseRates(rows);
  if (!typed.ok) return true;
  const key = (r: ShippingRate) => `${r.carrier}:${r.maxWeightG}:${r.priceOre}`;
  return typed.items.map(key).join('|') !== (Shipping.rates(stored).ok ? (Shipping.rates(stored) as { ok: true; value: ShippingRate[] }).value : stored).map(key).join('|');
}

/** A product's weight as typed («350», «1200»; grams) → number | null (empty = unknown), or a message. */
export function parseWeight(text: string): { ok: true; weightG: number | null } | { ok: false; message: string } {
  const s = String(text ?? '').trim().replace(/[\s\u00a0]/g, '');
  if (!s) return { ok: true, weightG: null };
  if (!/^\d{1,5}$/.test(s) || Number(s) < 1 || Number(s) > Shipping.MAX_PRODUCT_WEIGHT_G) return { ok: false, message: 'Vekten er et helt antall gram fra 1 til 50 000, eller tomt (regnes som 500 g).' };
  return { ok: true, weightG: Number(s) };
}

/** What the order's shipping block says of a weight: «1,7 kg» / «500 g». */
export const weightText = (g: number) => (g >= 1000 ? `${gramsText(g)} kg` : `${g} g`);

/** What staff are told after «Send» (POST …/ship): the capture's outcome as before, with the carrier. */
export function shipOutcomeText(status: number, body: Record<string, any>, carrier: string): { text: string; tone: 'ok' | 'warn' | 'err' } {
  const name = CARRIER_NO[carrier] || carrier;
  if (status === 200) {
    const amount = body && body.operation && typeof body.operation.amountOre === 'number' ? body.operation.amountOre : null;
    const tracked = body && body.shipping && body.shipping.trackingNumber ? ` Sporingsnummer ${body.shipping.trackingNumber}.` : '';
    return { tone: 'ok', text: `Sendt med ${name}.${amount !== null ? ` ${money(amount)} er trukket.` : ''}${tracked}${body && body.notified ? ' Kunden har fått beskjed.' : ''}` };
  }
  if (status === 202) return { tone: 'warn', text: `Merket som sendt med ${name}. Venter på svar fra Vipps om pengene – prøves igjen automatisk.` };
  const said = (body && (body.message || body.message_no)) || '';
  if (status === 0 || status >= 500) return { tone: 'err', text: `${said || 'Fikk ikke svar.'} Trykk «Prøv igjen» – det sendes som samme forespørsel.` };
  return { tone: 'err', text: said || 'Det gikk ikke.' };
}
